import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Row, Col } from "react-bootstrap"
import styled from "styled-components"

import { IDLE, PENDING, LANGUAGES, TABLE_EDITABLE_CELL } from "const"
import { LangIndex, StateType } from "client/utils"
import {
    StoreType,
    LangKey,
    TranslationList,

    LoadAllTranslations,
    SaveTranslations,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    TableWrapper,
    TableScroller,
    TableContentWrapper,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableCellContentWrapper,
    Button,
    SpinnerIcon,
    SelectDropdown,
    SelectDropdownItem,
    FileLoader,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmBottom,
    BalloonMessage,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import Lang from "client/store/lang"
import TableTextarea from "./textarea"

const TableContentWrapperStyled = styled(TableContentWrapper)`
    td, th {
        border: 1px solid ${(props) => props.theme.tableRowBorderColor};
        &::after {
            display: none;
        }
    }
`

type ActiveCell = {
    langId: LangIndex
    langKey: LangKey
}

const rowSize = "fit"
const cellWidth = "350px"
let prevActiveCell: ActiveCell = null

const MtTranslations = () => {
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)

    const [translationList, setTranslationList] = useState<TranslationList>({})
    const [activeCell, setActiveCell] = useState<ActiveCell>(null)

    const [showModal, setShowModal] = useState(false)
    const [targetLang, setTargetLang] = useState<LangIndex>(LANGUAGES[0] as LangIndex)
    const [translationJSON, setTranslationJSON] = useState<string>(null)

    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [uploadState, setUploadState] = useState<StateType>(IDLE)

    const clearActiveCell = () => {
        setActiveCell(null)
    }

    useEffect(() => {
        document.addEventListener("click", clearActiveCell)
        return () => document.removeEventListener("click", clearActiveCell)
    }, [])

    useEffect(() => {
        setLoadState(PENDING)
        LoadAllTranslations().then((res) => {
            setTranslationList(res)
            setLoadState(IDLE)
        })
    }, [])

    useEffect(() => {
        prevActiveCell = activeCell
    }, [activeCell])

    const onChange = (value: string) => {
        const valueTrim = value.trim()
        const { langId, langKey } = prevActiveCell

        // check if value the same
        if (translationList[langId][langKey] === valueTrim) return

        const translationListCopy = { ...translationList }
        if (valueTrim.length === 0) {
            if (translationListCopy[langId][langKey]) delete translationListCopy[langId][langKey]
        } else {
            translationListCopy[langId][langKey] = valueTrim
        }

        setTranslationList(translationListCopy)
        setLoadState(PENDING)
        SaveTranslations(translationListCopy).then(() => (
            LoadAllTranslations().then((res) => {
                setTranslationList(res)
                setLoadState(IDLE)
            })
        ))
    }

    const onAddFromFile = () => {
        if (!translationJSON) return
        const translationListCopy = { ...translationList }

        translationListCopy[targetLang] = {
            ...translationListCopy[targetLang],
            ...JSON.parse(translationJSON),
        }

        setUploadState(PENDING)
        SaveTranslations(translationListCopy).then(() => (
            LoadAllTranslations().then((res) => {
                setTranslationList(res)
                setUploadState(IDLE)

                // clear form
                setTranslationJSON(null)
            })
        ))
    }

    const closeModal = () => {
        setShowModal(false)
        setTranslationJSON(null)
    }

    if (userData.accountId !== 1) return <AccessDenied />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.translations}</PageTitle>
                <Button
                    variant="success"
                    onClick={() => setShowModal(true)}
                >
                    {lang.addFromFile}
                </Button>
            </PageHeader>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <TableWrapper>
                    <TableScroller>
                        <TableContentWrapperStyled>
                            <thead>
                                <TableRow noHover size={rowSize}>
                                    <TableHeaderCell key="langKey" cellMinWidth={cellWidth} />
                                    <TableHeaderCell key="langBaseValue" cellMinWidth={cellWidth}>
                                        {lang.defaultValue}
                                    </TableHeaderCell>
                                    {
                                        LANGUAGES.map((row) => {
                                            const langId = row as LangIndex
                                            return (
                                                <TableHeaderCell key={langId} cellMinWidth={cellWidth}>
                                                    <span>{lang[langId]}</span>
                                                </TableHeaderCell>
                                            )
                                        })
                                    }
                                </TableRow>
                            </thead>

                            <tbody>
                                {
                                    Object.keys(Lang).map((key) => {
                                        const langKey = key as LangKey
                                        return (
                                            <TableRow key={langKey} noHover size={rowSize}>
                                                <TableCell cellMinWidth={cellWidth}>
                                                    <TableCellContentWrapper>
                                                        {langKey}
                                                    </TableCellContentWrapper>
                                                </TableCell>
                                                <TableCell cellMinWidth={cellWidth}>
                                                    <TableCellContentWrapper>
                                                        {Lang[langKey]}
                                                    </TableCellContentWrapper>
                                                </TableCell>
                                                {
                                                    LANGUAGES.map((row) => {
                                                        const langId = row as LangIndex
                                                        const isEditable = activeCell
                                                            && activeCell.langId === langId
                                                            && activeCell.langKey === langKey

                                                        const value = (translationList[langId] && translationList[langId][langKey]) || null

                                                        return (
                                                            <TableCell
                                                                key={langId}
                                                                cellMinWidth={cellWidth}
                                                                isEditable={isEditable}
                                                                className={TABLE_EDITABLE_CELL}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    setActiveCell({
                                                                        langId,
                                                                        langKey,
                                                                    })
                                                                }}
                                                            >
                                                                {
                                                                    isEditable
                                                                        ? (
                                                                            <TableTextarea
                                                                                data={value}
                                                                                dataDefault={Lang[langKey]}
                                                                                onChange={(val) => onChange(val)}
                                                                                clearActiveCell={clearActiveCell}
                                                                            />
                                                                        )
                                                                        : <TableCellContentWrapper>{value}</TableCellContentWrapper>
                                                                }
                                                            </TableCell>
                                                        )
                                                    })
                                                }
                                            </TableRow>
                                        )
                                    })
                                }
                            </tbody>
                        </TableContentWrapperStyled>
                    </TableScroller>
                </TableWrapper>

                <ConfirmWrapper
                    show={showModal}
                    onClose={closeModal}
                >
                    <Confirm>
                        <ConfirmTitle>{lang.selectFile}</ConfirmTitle>
                        <Row>
                            <Col xs={4}>
                                <SelectDropdown title={lang[targetLang]}>
                                    {
                                        LANGUAGES.map((row) => {
                                            const langId = row as LangIndex
                                            return (
                                                <SelectDropdownItem key={langId} onClick={() => setTargetLang(langId)}>
                                                    {lang[langId]}
                                                </SelectDropdownItem>
                                            )
                                        })
                                    }
                                </SelectDropdown>
                            </Col>
                            <Col xs={8}>
                                <FileLoader
                                    type="file"
                                    forceAccept="application/json"
                                    value={null}
                                    autoLoad={false}
                                    onLoad={(file) => { // set "translationJSON" after the file is selected
                                        const reader = new FileReader()
                                        reader.readAsText(file)

                                        reader.onload = () => {
                                            setTranslationJSON(reader.result as string)
                                        }

                                        reader.onerror = () => {
                                            BalloonMessage("Error while opening the file. Check console")
                                            // eslint-disable-next-line no-console
                                            console.log(reader.error)
                                        }
                                    }}
                                    callback={(file) => { // clear "translationJSON" on delete
                                        if (!file) setTranslationJSON(null)
                                    }}
                                />
                            </Col>
                        </Row>

                        <ConfirmBottom>
                            <Button row onClick={closeModal}>{lang.cancel}</Button>
                            <Button variant="success" row onClick={onAddFromFile}>
                                {uploadState === PENDING ? <SpinnerIcon /> : lang.add}
                            </Button>
                        </ConfirmBottom>
                    </Confirm>
                </ConfirmWrapper>
            </PageContent>
        </Container>
    )
}
export default MtTranslations
